import { SyntheticEvent, useCallback, useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import Tab from "@mui/material/Tab";

import { FileUploadIcon, FillFormIcon } from "@dotnet/telematics-design-system";

import Dialog from "components/DesignComponents/Dialog";
import SegmentedToggle, {
    TAB_SIZES
} from "components/DesignComponents/SegmentedToggle";
import AlertContext from "context/alert/alertContext";
import DropzoneContext from "context/dropzone/dropzoneContext";
import ParamsContext from "context/params/paramsContext";
import { TABLE_NAMES, useApi, useMediaQueries } from "shared";

import { DEVICE_ACTIONS, DialogProps } from "../../../types";
import DialogActions from "../../components/DialogActions";
import AddDeviceFromFile from "./AddDeviceFromFile/AddDeviceFromFile";
import AddDeviceFromForm from "./AddDeviceFromForm/AddDeviceFromForm";
import { AddDevicePayload, TABS } from "./types";

const AddDevice = ({ isOpen, close }: DialogProps) => {
    const { removeAlertForBGAction, setBackgroundActionId } =
        useContext(AlertContext);

    const { setReloadItems } = useContext(ParamsContext);
    const { removeAllFiles } = useContext(DropzoneContext);

    const { t } = useTranslation();
    const { postData, handleResponse } = useApi();
    const { fromSm } = useMediaQueries();

    const [isLoading, setIsLoading] = useState(false);
    const [tabValue, setTabValue] = useState(TABS.FromForm);

    const [payloadInfo, setPayloadInfo] = useState<AddDevicePayload | null>(
        null
    );

    const isFormTab = tabValue === TABS.FromForm;
    const isFileTab = tabValue === TABS.FromFile;

    const handleChanges = (data: AddDevicePayload) =>
        data !== null ? setPayloadInfo(data) : setPayloadInfo(null);

    const handleCloseDialog = useCallback(() => {
        close(DEVICE_ACTIONS.AddDevice);
    }, [close]);

    const handleTabChange = (_: SyntheticEvent, newValue: TABS) => {
        setTabValue(newValue);
        setPayloadInfo(null);
        isFileTab && removeAllFiles();
    };

    const closeAndReload = useCallback(() => {
        handleCloseDialog();
        setReloadItems(TABLE_NAMES.Devices);
    }, [handleCloseDialog, setReloadItems]);

    const handleSubmit = useCallback(async () => {
        removeAlertForBGAction();

        if (payloadInfo) {
            const { resource, payload } = payloadInfo;

            setIsLoading(true);

            try {
                const response = await postData(resource, payload);

                if (isFormTab) {
                    closeAndReload();
                    handleResponse(response);
                } else {
                    setBackgroundActionId(
                        response.data,
                        TABLE_NAMES.Devices,
                        () => closeAndReload(),
                        () => setIsLoading(false)
                    );
                }
            } catch (error) {
                handleResponse(error);
                setIsLoading(false);
            }
        }
    }, [
        isFormTab,
        payloadInfo,
        closeAndReload,
        handleResponse,
        postData,
        setBackgroundActionId,
        removeAlertForBGAction
    ]);

    const isBgActionLoading = !isFormTab && isLoading;

    return (
        <Dialog
            data-testid="add-device-data-dialog"
            extendToMaxWidth
            title={t("Dialog##add new device")}
            description={t("Dialog##add new device description")}
            isTitleSeparator
            isActionsSeparator
            open={isOpen}
            close={isBgActionLoading ? undefined : handleCloseDialog}
            submit={handleSubmit}
            slotProps={{
                transition: {
                    onExited: () => {
                        removeAlertForBGAction();
                        setIsLoading(false);
                        setTabValue(TABS.FromForm);
                        removeAllFiles();
                        setPayloadInfo(null);
                    }
                }
            }}
            actions={
                <DialogActions
                    onClose={handleCloseDialog}
                    isLoading={isBgActionLoading || isLoading}
                    isReadyToConfirm={payloadInfo !== null}
                    confirmationButtonText={t("Button##add")}
                    cancelButtonTestId="cancel-add-device"
                    confirmationButtonTestId="add-device-button"
                />
            }
        >
            <SegmentedToggle
                data-testid="add-device-tabs"
                value={tabValue}
                size={TAB_SIZES.Small}
                fullWidth={fromSm}
                tabWidth="277px"
                onChange={handleTabChange}
                customStyle={{
                    marginTop: "8px"
                }}
            >
                <Tab
                    data-testid="add-device-form-tab"
                    label={t("Dialog##from form")}
                    icon={<FillFormIcon />}
                    disabled={isLoading}
                />

                <Tab
                    data-testid="add-device-file-tab"
                    label={t("Dialog##from file")}
                    icon={<FileUploadIcon />}
                    disabled={isLoading}
                />
            </SegmentedToggle>

            {isFormTab && (
                <AddDeviceFromForm
                    isLoading={isLoading}
                    setDevicePayload={handleChanges}
                />
            )}

            {isFileTab && (
                <AddDeviceFromFile
                    isLoading={isLoading}
                    setDevicePayload={handleChanges}
                />
            )}
        </Dialog>
    );
};

export default AddDevice;
